import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import { store } from './store';
import { initPosthog } from './lib/posthog';

// Initialize i18n before app render
import './i18n';

import './styles.css';
// 1. Base & Tokens - MUST load first
import './styles/base.css';
// 2. Primitives - Depends on base tokens
import './styles/primitives.css';
// 3. Layout & Utilities
import './styles/layout.css';
import './styles/utilities.css';
// 4. Common component patterns
import './styles/components/common.css';
import './styles/components/forms.css';
import './styles/components/auth.css';
// 6. Responsive - MUST load last
import './styles/responsive.css';
// Android Material Design 3 (applied when root has .android-app on native Android)
import './styles/android.css';
import './styles/mobile-native.css';

// Import providers
import { AppProviders } from './app/AppProviders';

initPosthog();

const sentryDsn = (import.meta as any).env?.VITE_SENTRY_DSN as string | undefined;
let captureSentryException: ((error: unknown, context?: Record<string, unknown>) => void) | null = null;

const scheduleLowPriorityTask = (task: () => void) => {
  if (typeof window === 'undefined') return;
  const browserWindow = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  };
  const runTask = () => browserWindow.setTimeout(task, 0);
  if (typeof browserWindow.requestIdleCallback === 'function') {
    browserWindow.requestIdleCallback(runTask, { timeout: 4000 });
    return;
  }
  browserWindow.addEventListener('load', runTask, { once: true });
};

if (sentryDsn && import.meta.env.PROD) {
  scheduleLowPriorityTask(() => {
    void import('@sentry/capacitor')
      .then((Sentry) => {
        Sentry.init({
          dsn: sentryDsn,
          enabled: true,
        });
        captureSentryException = (error: unknown, context?: Record<string, unknown>) => {
          Sentry.captureException(error, context ? { extra: context } : undefined);
        };
      })
      .catch((error) => {
        console.warn('Failed to initialize Sentry', error);
      });
  });
}

// Error boundary component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null; errorInfo: React.ErrorInfo | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
    captureSentryException?.(error, { componentStack: errorInfo?.componentStack });
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', fontFamily: '"Outfit", -apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ color: '#ef4444' }}>Application Error</h1>
          <p style={{ color: '#6b7280', marginTop: '12px' }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          {this.state.error?.stack && (
            <pre style={{
              background: '#f3f4f6',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '16px'
            }}>
              {this.state.error.stack}
            </pre>
          )}
          {this.state.errorInfo?.componentStack && (
            <pre style={{
              background: '#f3f4f6',
              padding: '16px',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '16px'
            }}>
              Component Stack:
              {this.state.errorInfo.componentStack}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#0F766E',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Add error handler for unhandled errors
window.addEventListener('error', (event) => {
  console.error('Unhandled error:', event.error);
  if (event.error) captureSentryException?.(event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  captureSentryException?.(event.reason);
});

const shouldDisableServiceWorker = (() => {
  if (typeof window === 'undefined') return false;
  const query = new URLSearchParams(window.location.search);
  return query.has('perf') || query.has('e2e') || Boolean(navigator.webdriver);
})();

// Register service worker in production unless the runtime is an automated perf/e2e pass.
if (import.meta.env.PROD && !shouldDisableServiceWorker) {
  void import('./services/swRegistration')
    .then(({ registerServiceWorker }) => {
      registerServiceWorker();
    })
    .catch((error) => {
      console.warn('Failed to register service worker', error);
    });
} else if (import.meta.env.PROD && shouldDisableServiceWorker) {
  console.info('[SW] Service worker disabled for perf/e2e runtime');
}

// PERFORMANCE: Add global visibility listener to pause CSS animations when tab is hidden
// This reduces CPU usage by ~10% when the app is in background
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    document.documentElement.classList.add('tab-hidden');
  } else {
    document.documentElement.classList.remove('tab-hidden');
  }
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

try {
  const appTree = (
    <Provider store={store}>
      <ErrorBoundary>
        <AppProviders>
          <App />
        </AppProviders>
      </ErrorBoundary>
    </Provider>
  );
  const enableStrictMode = import.meta.env.DEV && (import.meta as any).env?.VITE_REACT_STRICT_MODE === 'true';

  ReactDOM.createRoot(rootElement).render(
    enableStrictMode ? <React.StrictMode>{appTree}</React.StrictMode> : appTree
  );
} catch (error) {
  console.error('Failed to render app:', error);
  rootElement.innerHTML = `
    <div style="padding: 40px; font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif; max-width: 600px; margin: 0 auto;">
      <h1 style="color: #ef4444;">Failed to load application</h1>
      <p style="color: #6b7280; margin-top: 12px;">${error instanceof Error ? error.message : 'Unknown error'}</p>
      <button onclick="window.location.reload()" style="margin-top: 20px; padding: 10px 20px; background: #0F766E; color: white; border: none; border-radius: 8px; cursor: pointer;">
        Reload Page
      </button>
    </div>
  `;
}
